import Reveal from "./Reveal";
import { Skeleton } from "./Skeleton";
import Bar from "./Bar";

export interface MiniBar {
  label: string;
  value: number;
  color?: string;
}

// Compact axis-free bars — horizontal rows or vertical columns, biggest
// bar sets the scale. Div-based like BarChart so labels lay out for free.
//
// While `loading`, the bars are real elements sized to zero and the figures are
// placeholders. Because the same divs stay mounted, `anim-grow-x` / `anim-grow-y`
// grow them into place when the data arrives rather than a ghost being replaced
// by a finished chart. Callers pass placeholder rows (labels and colours are
// known ahead of the data) so the axis and legend do not move on the swap.
export default function MiniBarChart({
  data,
  direction = "horizontal",
  height = 96,
  loading = false,
}: {
  data: MiniBar[];
  direction?: "horizontal" | "vertical";
  height?: number;
  loading?: boolean;
}) {
  const max = Math.max(...data.map((d) => d.value)) || 1;

  if (direction === "vertical") {
    return (
      <Reveal>
      <div className="flex items-end justify-between gap-3" style={{ height }}>
        {data.map((d) => (
          <div key={d.label} className="flex-1 h-full flex flex-col items-center justify-end gap-1.5">
            {loading ? (
              <Skeleton className="h-3 w-7 rounded" />
            ) : (
              <span className="text-[11.5px] font-semibold text-text tabular-nums">
                {d.value.toLocaleString("en-US")}
              </span>
            )}
            {/* min-h-0 lets the track shrink inside the flex column so the
                bar scales against the chart height, not its own content. */}
            <div className="w-full max-w-9.5 flex-1 min-h-0">
              <Bar
                fraction={d.value / max}
                color={d.color}
                direction="vertical"
                loading={loading}
              />
            </div>
            <span className="text-[10.5px] text-text-faint truncate max-w-full">{d.label}</span>
          </div>
        ))}
      </div>
      </Reveal>
    );
  }

  return (
    <Reveal>
    <div className="flex flex-col gap-2.5">
      {data.map((d) => (
        <div key={d.label} className="flex items-center gap-2.5">
          <span className="w-14 shrink-0 text-[11.5px] text-text-muted truncate">{d.label}</span>
          <div className="flex-1">
            {/* Compact rows keep a slimmer track than the standard h-7. */}
            <Bar fraction={d.value / max} color={d.color} loading={loading} thickness="h-5" />
          </div>
          {loading ? (
            <Skeleton className="h-3 w-9 shrink-0 rounded" />
          ) : (
            <span className="w-9 shrink-0 text-right text-[12px] font-semibold text-text tabular-nums">
              {d.value.toLocaleString("en-US")}
            </span>
          )}
        </div>
      ))}
    </div>
    </Reveal>
  );
}
